import React, { useEffect, useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2, 
  XCircle, 
  ArrowLeft, 
  Trophy, 
  Lightbulb, 
  Loader2, 
  Bot, 
  BarChart3, 
  AlertTriangle,
  RefreshCw,
  PieChart,
  Save,
  Database,
  BookOpen,
  FileText,
  Info
} from "lucide-react";
import type { Question } from "@/types/quiz";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import GenerateFlashcardsFromMistakes from "@/components/quiz/GenerateFlashcardsFromMistakes";
import { useAuthSession } from "@/hooks/useAuthSession";
import { Progress } from "@/components/ui/progress";
import { useQuizSessionHistory } from "@/hooks/useQuizSessionHistory";
import { generateUUID } from "@/utils/uuid";
import MathRenderer from "@/components/mock-test/MathRenderer";

interface EnhancedResultsProps {
  questions: Question[];
  answers: (number | null)[];
  onReset: () => void;
  subject?: string | null;
  category?: string | null;
  difficulty?: string;
  sessionId?: string;
}

const EnhancedResults: React.FC<EnhancedResultsProps> = ({ 
  questions, 
  answers, 
  onReset,
  subject,
  category,
  difficulty = 'medium',
  sessionId
}) => {
  const [aiExplanations, setAiExplanations] = useState<Record<number, string>>({});
  const [loadingExplanations, setLoadingExplanations] = useState<Record<number, boolean>>({});
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [resultSaved, setResultSaved] = useState(false);
  const [resultSaving, setResultSaving] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const { session } = useAuthSession();
  const [animateScore, setAnimateScore] = useState(0);
  const { isResultSubmitted, markResultAsSubmitted } = useQuizSessionHistory();
  const resultSubmissionAttemptedRef = useRef(false);
  const databaseSaveAttemptedRef = useRef(false);

  useEffect(() => {
    console.log("EnhancedResults received: ", {
      questions: questions?.length,
      answers: answers?.length,
      firstQuestion: questions?.[0],
      subject,
      category,
      difficulty,
      sessionId,
      questionIds: questions?.map(q => q.id)
    });
    
    if (questions && questions.length > 0) {
      console.log("Questions in EnhancedResults:", questions.map(q => ({ 
        id: q.id, 
        difficulty: q.difficulty,
        question: q.question?.substring(0, 30) + "..." 
      })));
    }
    
    if (answers && answers.length > 0) {
      console.log("Answers in EnhancedResults:", answers);
    }
  }, [questions, answers, subject, category, difficulty, sessionId]);

  const validQuestions = (questions && Array.isArray(questions))
    ? questions.filter(q => q && typeof q === 'object').map(q => ({...q}))
    : [];
  
  const answeredQuestions = validQuestions.filter((_, index) => 
    answers[index] !== null && answers[index] !== undefined
  );
  
  let validAnswers = (answers && Array.isArray(answers))
    ? [...answers]
    : [];
    
  while (validAnswers.length < validQuestions.length) {
    validAnswers.push(null);
  }
  
  if (validAnswers.length > validQuestions.length) {
    validAnswers = validAnswers.slice(0, validQuestions.length);
  }

  useEffect(() => {
    console.log("Valid questions in EnhancedResults:", validQuestions.length);
    console.log("Valid answers in EnhancedResults:", validAnswers.length);
    console.log("Question IDs:", validQuestions.map(q => q.id));
    
    validQuestions.forEach((q, idx) => {
      console.log(`Q${idx}: "${q.question?.substring(0, 30)}..." - ID: ${q.id}, Difficulty: ${q.difficulty}, Correct: ${q.correctanswer}, User: ${validAnswers[idx]}`);
    });
  }, [validQuestions, validAnswers]);

  const correctAnswers = validAnswers.reduce((total, answer, index) => {
    if (answer === undefined || answer === null || index >= validQuestions.length) return total;
    return answer === validQuestions[index]?.correctanswer ? total + 1 : total;
  }, 0);

  const percentage = validQuestions.length > 0 
    ? Math.round((correctAnswers / validQuestions.length) * 100) 
    : 0;
    
  const incorrectAnswers = validQuestions.length - correctAnswers;

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimateScore(prev => {
        if (prev < percentage) return prev + 1;
        clearInterval(interval);
        return percentage;
      });
    }, 30);
    
    return () => clearInterval(interval);
  }, [percentage]);

  const handleManualSave = async () => {
    if (!session?.user || !validQuestions.length) {
      toast.error("You must be logged in to save results");
      return;
    }
    
    if (resultSaved) {
      toast.info("Result already saved!");
      return;
    }
    
    if (answeredQuestions.length === 0 || validAnswers.every(a => a === null)) {
      toast.error("No answered questions to save");
      return;
    }
    
    try {
      setManualSaving(true);
      
      const resultId = sessionId || generateUUID();
      
      console.log('Manually saving quiz result to database:', {
        id: resultId,
        user_id: session.user.id,
        score: correctAnswers,
        total_questions: validQuestions.length,
        percentage: percentage,
        category: category || validQuestions[0]?.category || 'General',
        subject: subject || validQuestions[0]?.subject || 'General',
        difficulty: difficulty,
        questionsCount: validQuestions.length,
        answersCount: validAnswers.filter(a => a !== null).length
      });

      const { error } = await supabase.from('quiz_results').insert({
        id: resultId,
        user_id: session.user.id,
        score: correctAnswers,
        total_questions: validQuestions.length,
        percentage: percentage,
        category: category || validQuestions[0]?.category || 'General',
        subject: subject || validQuestions[0]?.subject || 'General',
        difficulty: difficulty,
        questions: validQuestions,
        answers: validAnswers,
        completed_at: new Date().toISOString()
      });

      if (error) {
        console.error('Error saving quiz result to database:', error);
        throw error;
      }
      
      setResultSaved(true);
      markResultAsSubmitted(); // Mark as submitted in the global state
      toast.success('Quiz result saved to your history!');
    } catch (error) {
      console.error('Failed to save quiz result to database:', error);
      toast.error('Failed to save quiz result');
    } finally {
      setManualSaving(false);
    }
  };

  const getAIExplanation = async (questionIndex: number) => {
    if (loadingExplanations[questionIndex]) return;
    
    setLoadingExplanations(prev => ({ ...prev, [questionIndex]: true }));
    try {
      const question = validQuestions[questionIndex];
      if (!question) {
        throw new Error('Question not found');
      }
      
      const { data, error } = await supabase.functions.invoke('ai-tutoring', {
        body: { 
          action: 'explain',
          data: { 
            question: question.question,
            options: question.options,
            correctAnswer: question.options[question.correctanswer],
            subject: question.subject || 'general'
          }
        }
      });

      if (error) throw error;
      setAiExplanations(prev => ({ ...prev, [questionIndex]: data.explanation }));
      toast.success('Explanation generated!');
    } catch (error) {
      console.error('Error getting explanation:', error);
      toast.error('Failed to generate explanation');
    } finally {
      setLoadingExplanations(prev => ({ ...prev, [questionIndex]: false }));
    }
  };

  const getWeakAreas = () => {
    if (!validQuestions.length || !validAnswers.length) {
      return [];
    }

    const wrongAnswers = validAnswers
      .map((answer, index) => {
        if (index >= validQuestions.length) return null;
        
        const question = validQuestions[index];
        if (!question) return null;
        
        return {
          question,
          isCorrect: answer === question.correctanswer
        };
      })
      .filter(item => item && !item.isCorrect);
    
    const categories: Record<string, number> = {};
    
    wrongAnswers.forEach(item => {
      if (item && item.question && item.question.category) {
        const category = item.question.category || 'General';
        categories[category] = (categories[category] || 0) + 1;
      }
    });
    
    return Object.entries(categories)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  };

  const formatExplanationText = (text: string) => {
    if (!text) return [];
    
    // Enhanced text cleaning to remove HTML tags and special characters
    let cleanedText = text
      // Remove HTML-like tags completely
      .replace(/<\/?span[^>]*>/gi, '')
      .replace(/<\/?div[^>]*>/gi, '')
      .replace(/<\/?p[^>]*>/gi, '')
      .replace(/<\/?strong[^>]*>/gi, '**')
      .replace(/<\/?em[^>]*>/gi, '*')
      .replace(/<\/?b[^>]*>/gi, '**')
      .replace(/<\/?i[^>]*>/gi, '*')
      // Clean up escaped characters
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#x27;/g, "'")
      .replace(/&apos;/g, "'")
      // Clean up markdown formatting
      .replace(/\*\*\*+/g, '**')
      .replace(/\\n/g, '\n')
      .replace(/\\\//g, '/')
      // Remove any remaining HTML-like patterns
      .replace(/<[^>]*>/g, '')
      // Remove special characters that might cause display issues
      .replace(/[""'']/g, '"')
      .replace(/[–—]/g, '-')
      .replace(/…/g, '...')
      .replace(/\u00a0/g, ' ')
      .replace(/\u2013/g, '-')
      .replace(/\u2014/g, '-')
      .replace(/\u2018/g, "'")
      .replace(/\u2019/g, "'")
      .replace(/\u201c/g, '"')
      .replace(/\u201d/g, '"')
      .replace(/\u2026/g, '...')
      .trim();

    // Split into sections by double line breaks
    const sections = cleanedText.split(/\n\n+/).filter(section => section.trim());
    
    const formattedSections = [];
    
    for (let section of sections) {
      const trimmedSection = section.trim();
      if (!trimmedSection) continue;
      
      // Handle main headings (# or ###)
      if (trimmedSection.match(/^#{1,3}\s+/)) {
        const level = (trimmedSection.match(/^#+/) || [''])[0].length;
        let content = trimmedSection.replace(/^#+\s*/, '');
        
        // Remove any remaining markdown formatting
        content = content.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
        
        formattedSections.push({
          type: level === 1 ? 'main-heading' : level === 2 ? 'sub-heading' : 'section-heading',
          content: content
        });
        continue;
      }
      
      // Handle evaluation section headers
      if (trimmedSection.match(/evaluation of each option|analysis of options|option evaluation|why other options are incorrect|additional context|context|background information|key concepts/i)) {
        let headerText = "Evaluation of Each Option";
        let headerIcon = BarChart3;
        
        if (trimmedSection.toLowerCase().includes("incorrect")) {
          headerText = "Why Other Options are Incorrect";
          headerIcon = XCircle;
        } else if (trimmedSection.toLowerCase().includes("additional context") || 
                   trimmedSection.toLowerCase().includes("context") ||
                   trimmedSection.toLowerCase().includes("background")) {
          headerText = "Additional Context";
          headerIcon = Info;
        } else if (trimmedSection.toLowerCase().includes("key concepts")) {
          headerText = "Key Concepts";
          headerIcon = BookOpen;
        }
        
        formattedSections.push({
          type: 'evaluation-header',
          content: headerText,
          icon: headerIcon
        });
        continue;
      }
      
      // Handle correct answer sections
      if (trimmedSection.match(/^(correct answer|the correct answer is|answer:|solution:)/i)) {
        let content = trimmedSection.replace(/^(correct answer|the correct answer is|answer:|solution:)[\s:]*/i, '');
        // Clean up any remaining formatting
        content = content.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
        
        formattedSections.push({
          type: 'correct-answer',
          content: content
        });
        continue;
      }
      
      // Handle numbered options (1., 2., etc.)
      const optionMatch = trimmedSection.match(/^(\d+)\.\s*(.+)/s);
      if (optionMatch) {
        const optionNumber = parseInt(optionMatch[1]);
        let optionContent = optionMatch[2].trim();
        
        // Extract title from quotes or bold text
        let optionTitle = '';
        const quoteMatch = optionContent.match(/^["""]([^"""]+)["""]/);
        const boldMatch = optionContent.match(/^\*\*([^*]+)\*\*/);
        
        if (quoteMatch) {
          optionTitle = quoteMatch[1];
          optionContent = optionContent.replace(/^["""]([^"""]+)["""]/, '').trim();
        } else if (boldMatch) {
          optionTitle = boldMatch[1];
          optionContent = optionContent.replace(/^\*\*([^*]+)\*\*/, '').trim();
        } else {
          // Use first sentence as title if no explicit formatting
          const sentences = optionContent.split(/[.!?]/);
          if (sentences.length > 1) {
            optionTitle = sentences[0] + '.';
            optionContent = sentences.slice(1).join('.').trim();
          } else {
            optionTitle = optionContent.substring(0, 60) + (optionContent.length > 60 ? '...' : '');
          }
        }
        
        // Clean up explanation content
        optionContent = optionContent
          .replace(/^[\s:,-]*/, '')
          .replace(/why it'?s (wrong|incorrect|not correct)[\s:,-]*/i, '')
          .replace(/why it'?s (right|correct)[\s:,-]*/i, '')
          .replace(/\*\*(.*?)\*\*/g, '$1')
          .replace(/\*(.*?)\*/g, '$1')
          .trim();
        
        // Clean up title
        optionTitle = optionTitle.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
        
        // Determine if this option is correct or incorrect
        const isCorrect = !trimmedSection.toLowerCase().includes('wrong') && 
                         !trimmedSection.toLowerCase().includes('incorrect') &&
                         (trimmedSection.toLowerCase().includes('correct') || 
                          trimmedSection.toLowerCase().includes('right'));
        
        formattedSections.push({
          type: 'option-explanation',
          number: optionNumber,
          title: optionTitle,
          content: optionContent,
          isCorrect: isCorrect
        });
        continue;
      }
      
      // Handle bullet points
      if (trimmedSection.includes('\n- ') || trimmedSection.startsWith('- ')) {
        const points = trimmedSection.split(/\n- /).map(point => {
          let cleanPoint = point.replace(/^- /, '').trim();
          // Clean up formatting
          cleanPoint = cleanPoint.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
          return cleanPoint;
        }).filter(point => point);
        
        formattedSections.push({
          type: 'bullet-list',
          points: points
        });
        continue;
      }
      
      // Handle emphasized text
      if (trimmedSection.includes('**')) {
        // Convert markdown bold to emphasized styling but clean the text
        const processedContent = trimmedSection.replace(/\*\*(.*?)\*\*/g, '$1');
        formattedSections.push({
          type: 'emphasized-text',
          content: processedContent
        });
        continue;
      }
      
      // Enhanced paragraph handling
      const isAdditionalContext = trimmedSection.toLowerCase().includes('additional context') ||
                                 trimmedSection.toLowerCase().includes('background') ||
                                 trimmedSection.toLowerCase().includes('context') ||
                                 trimmedSection.toLowerCase().includes('note:') ||
                                 trimmedSection.toLowerCase().includes('remember:');
      
      // Clean up any remaining formatting for regular paragraphs
      const processedContent = trimmedSection.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
      
      formattedSections.push({
        type: isAdditionalContext ? 'additional-context' : 'paragraph',
        content: processedContent
      });
    }
    
    return formattedSections;
  };

  const renderFormattedContent = (content: string) => {
    // Always use MathRenderer as it can handle both mathematical expressions and plain text
    // MathRenderer will properly render LaTeX expressions like $\theta$, \tan\theta, etc.
    return <MathRenderer text={content} />;
  };

  const handleNext = () => {
    if (currentReviewIndex < validQuestions.length - 1) {
      setCurrentReviewIndex(currentReviewIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentReviewIndex > 0) {
      setCurrentReviewIndex(currentReviewIndex - 1);
    }
  };

  const weakAreas = getWeakAreas();
  
  const safeCurrentIndex = Math.min(currentReviewIndex || 0, validQuestions.length - 1);
  const currentQuestion = validQuestions[safeCurrentIndex];
  const userAnswer = validAnswers[safeCurrentIndex];
  
  if (!validQuestions.length) {
    return (
      <Card className="p-6 text-center max-w-xl mx-auto">
        <Loader2 className="w-10 h-10 mx-auto mb-4 animate-spin text-primary" />
        <p>Loading quiz results...</p>
      </Card>
    );
  }

  console.log("Rendering final results with questions:", validQuestions.length);
  console.log("And answers:", validAnswers.length);

  const getScoreMessage = () => {
    if (percentage >= 90) return "Excellent! You've mastered this topic.";
    if (percentage >= 75) return "Great job! You have a strong understanding.";
    if (percentage >= 60) return "Good effort! Keep practicing to improve.";
    if (percentage >= 40) return "You're making progress. Keep going!";
    return "Keep practicing! You'll get better with more study.";
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      <Card className="p-6 shadow-lg border-t-4 border-primary rounded-xl overflow-hidden bg-white">
        <div className="text-center mb-6">
          <div className="relative w-24 h-24 mx-auto mb-4">
            <div className="absolute inset-0">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle 
                  className="text-gray-200" 
                  strokeWidth="8" 
                  stroke="currentColor" 
                  fill="transparent" 
                  r="42" 
                  cx="50" 
                  cy="50" 
                />
                <circle 
                  className={cn(
                    "text-primary transition-all duration-1000 ease-out",
                    percentage < 50 && "text-red-500",
                    percentage >= 50 && percentage < 75 && "text-amber-500",
                    percentage >= 75 && "text-green-500"
                  )}
                  strokeWidth="8" 
                  strokeDasharray={264}
                  strokeDashoffset={264 - (264 * animateScore) / 100}
                  strokeLinecap="round" 
                  stroke="currentColor" 
                  fill="transparent" 
                  r="42" 
                  cx="50" 
                  cy="50" 
                />
              </svg>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold">{animateScore}%</span>
            </div>
          </div>
          
          <h2 className="text-3xl font-bold mb-2 text-gray-800">Quiz Complete!</h2>
          
          <div className="text-lg mb-4 bg-gradient-to-r from-primary/90 to-primary/70 text-transparent bg-clip-text font-bold">
            Your Score: {correctAnswers} out of {validQuestions.length}
          </div>
          
          <div className={cn(
            "text-lg font-medium p-3 rounded-lg",
            percentage >= 80 ? "bg-green-100 text-green-700" :
            percentage >= 60 ? "bg-amber-100 text-amber-700" :
            "bg-red-100 text-red-700"
          )}>
            {getScoreMessage()}
          </div>
          
          <div className="mt-4">
            <Button
              onClick={handleManualSave}
              disabled={resultSaved || manualSaving || validQuestions.length === 0}
              className={cn(
                "flex items-center gap-2 px-5 py-2",
                resultSaved ? "bg-green-500 hover:bg-green-600" : "bg-blue-500 hover:bg-blue-600"
              )}
            >
              {manualSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : resultSaved ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Saved to History
                </>
              ) : (
                <>
                  <Database className="w-4 h-4" />
                  Save Result to History
                </>
              )}
            </Button>
          </div>
          
          {resultSaving && (
            <div className="flex items-center justify-center mt-2 text-sm text-gray-500">
              <Loader2 className="w-3 h-3 mr-2 animate-spin" />
              Saving your result...
            </div>
          )}
          
          {resultSaved && (
            <div className="flex items-center justify-center mt-2 text-sm text-green-600">
              <CheckCircle2 className="w-3 h-3 mr-2" />
              Result saved successfully
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-green-700 font-medium">Correct</h3>
              <span className="text-lg font-bold text-green-700">{correctAnswers}</span>
            </div>
            <Progress 
              value={correctAnswers / validQuestions.length * 100} 
              className="h-2 mt-2" 
              indicatorClassName="bg-green-500" 
            />
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-red-700 font-medium">Incorrect</h3>
              <span className="text-lg font-bold text-red-700">{incorrectAnswers}</span>
            </div>
            <Progress 
              value={incorrectAnswers / validQuestions.length * 100} 
              className="h-2 mt-2" 
              indicatorClassName="bg-red-500" 
            />
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-blue-700 font-medium">Accuracy</h3>
              <span className="text-lg font-bold text-blue-700">{percentage}%</span>
            </div>
            <Progress 
              value={percentage} 
              className="h-2 mt-2" 
              indicatorClassName="bg-blue-500" 
            />
          </div>
        </div>

        {weakAreas.length > 0 && (
          <div className="mb-6 bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h3 className="text-lg font-semibold text-amber-800 flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5" />
              Areas to Improve
            </h3>
            <ul className="space-y-2">
              {weakAreas.map(([area, count]) => (
                <li key={area} className="flex justify-between items-center">
                  <span className="text-amber-700">{area}</span>
                  <span className="bg-amber-200 text-amber-800 px-2 py-1 rounded-full text-xs font-medium">
                    {count} {count === 1 ? 'question' : 'questions'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-8">
          <GenerateFlashcardsFromMistakes 
            questions={validQuestions} 
            answers={validAnswers} 
            subject={subject || validQuestions[0]?.subject || "General"}
            category={category || validQuestions[0]?.category || "General"}
          />
        </div>
        
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Review Your Answers ({validQuestions.length} Questions)
            </h3>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
                disabled={safeCurrentIndex === 0}
                className="h-8 px-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm bg-gray-100 px-3 py-1 rounded-full">
                {safeCurrentIndex + 1} / {validQuestions.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                disabled={safeCurrentIndex === validQuestions.length - 1}
                className="h-8 px-2"
              >
                Next
                <ArrowLeft className="w-4 h-4 ml-1 rotate-180" />
              </Button>
            </div>
          </div>
          
          {currentQuestion && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="mb-3">
                <span className="inline-block bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs mb-2">
                  Question {safeCurrentIndex + 1}
                </span>
                <p className="font-medium text-gray-800">{currentQuestion?.question}</p>
              </div>
              
              <div className="space-y-2 mb-4">
                {currentQuestion?.options.map((option, index) => (
                  <div
                    key={index}
                    className={cn(
                      "p-3 rounded-lg border-2 transition-colors flex justify-between items-center",
                      index === currentQuestion.correctanswer 
                        ? "border-green-500 bg-green-50" 
                        : index === userAnswer && index !== currentQuestion.correctanswer
                          ? "border-red-500 bg-red-50"
                          : "border-gray-200"
                    )}
                  >
                    <span className={
                      index === currentQuestion.correctanswer
                        ? "text-green-700"
                        : index === userAnswer && index !== currentQuestion.correctanswer
                          ? "text-red-700"
                          : "text-gray-700"
                    }>
                      {option}
                    </span>
                    
                    {index === currentQuestion.correctanswer && (
                      <CheckCircle2 className="w-5 h-5 text-green-500 ml-2 flex-shrink-0" />
                    )}
                    
                    {index === userAnswer && index !== currentQuestion.correctanswer && (
                      <XCircle className="w-5 h-5 text-red-500 ml-2 flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
              
              {currentQuestion?.explanation && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mt-4">
                  <h4 className="font-medium text-blue-700 flex items-center gap-2 mb-1">
                    <Lightbulb className="w-4 h-4" />
                    Explanation
                  </h4>
                  <p className="text-blue-700 text-sm">{currentQuestion.explanation}</p>
                </div>
              )}
            </div>
          )}
          
          <div>
            {!aiExplanations[safeCurrentIndex] ? (
              <Button
                variant="outline"
                onClick={() => getAIExplanation(safeCurrentIndex)}
                disabled={loadingExplanations[safeCurrentIndex]}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white hover:text-white hover:bg-none"
              >
                {loadingExplanations[safeCurrentIndex] ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Bot className="w-4 h-4 mr-2" />
                )}
                Get AI Explanation
              </Button>
            ) : (
              <div className="mt-4 border rounded-lg overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 p-4 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <Bot className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold">AI Explanation</h4>
                      <p className="text-purple-100 text-sm">Detailed breakdown and analysis</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 p-6 space-y-6">
                  {formatExplanationText(aiExplanations[safeCurrentIndex]).map((item, i) => {
                    if (item.type === 'main-heading') {
                      return (
                        <div key={i} className="border-b-2 border-gradient-to-r from-blue-200 to-purple-200 pb-4">
                          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
                            {renderFormattedContent(item.content)}
                          </h2>
                        </div>
                      );
                    } 
                    else if (item.type === 'sub-heading') {
                      return (
                        <div key={i} className="mt-6 mb-4">
                          <h3 className="text-xl font-semibold text-slate-700 flex items-center gap-3">
                            <div className="w-0.5 h-6 bg-gradient-to-b from-emerald-500 to-teal-500 rounded-full"></div>
                            {renderFormattedContent(item.content)}
                          </h3>
                        </div>
                      );
                    }
                    else if (item.type === 'section-heading') {
                      return (
                        <div key={i} className="mt-4 mb-3">
                          <h4 className="text-lg font-medium text-slate-600 flex items-center gap-2">
                            <div className="w-0.5 h-5 bg-gradient-to-b from-amber-500 to-orange-500 rounded-full"></div>
                            {renderFormattedContent(item.content)}
                          </h4>
                        </div>
                      );
                    }
                    else if (item.type === 'evaluation-header') {
                      const Icon = item.icon || BarChart3;
                      return (
                        <div key={i} className="mt-8 mb-6">
                          <div className="bg-gradient-to-r from-indigo-100 via-purple-50 to-pink-100 p-5 rounded-xl border-l-4 border-indigo-400 shadow-sm">
                            <h3 className="text-xl font-bold text-indigo-800 flex items-center gap-3">
                              <div className="p-2 bg-indigo-200 rounded-lg">
                                <Icon className="h-6 w-6 text-indigo-700" />
                              </div>
                              {item.content}
                            </h3>
                          </div>
                        </div>
                      );
                    }
                    else if (item.type === 'correct-answer') {
                      return (
                        <div key={i} className="mt-6 mb-6">
                          <div className="bg-gradient-to-r from-emerald-100 to-green-100 p-6 rounded-xl border-l-4 border-emerald-500 shadow-md">
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0">
                                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                                  <CheckCircle2 className="h-7 w-7 text-white" />
                                </div>
                              </div>
                              <div className="flex-1">
                                <h4 className="font-bold text-emerald-800 mb-3 text-lg flex items-center gap-2">
                                  <Trophy className="h-5 w-5" />
                                  Correct Answer
                                </h4>
                                <div className="text-emerald-700 leading-relaxed text-base">
                                  {renderFormattedContent(item.content)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    else if (item.type === 'option-explanation') {
                      return (
                        <div key={i} className="mt-5 mb-4">
                          <div className={cn(
                            "p-5 rounded-xl border-l-4 shadow-sm transition-all duration-200 hover:shadow-md",
                            item.isCorrect 
                              ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-400" 
                              : "bg-gradient-to-r from-red-50 to-rose-50 border-red-400"
                          )}>
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0">
                                <div className={cn(
                                  "w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-md",
                                  item.isCorrect 
                                    ? "bg-gradient-to-br from-green-500 to-emerald-600 text-white" 
                                    : "bg-gradient-to-br from-red-500 to-rose-600 text-white"
                                )}>
                                  {item.number}
                                </div>
                              </div>
                              <div className="flex-1">
                                <h5 className={cn(
                                  "font-semibold mb-2 text-lg flex items-center gap-2",
                                  item.isCorrect ? "text-green-800" : "text-red-800"
                                )}>
                                  {item.isCorrect ? (
                                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                                  ) : (
                                    <XCircle className="h-5 w-5 text-red-600" />
                                  )}
                                  Option {item.number}: {renderFormattedContent(item.title)}
                                </h5>
                                <div className={cn(
                                  "leading-relaxed",
                                  item.isCorrect ? "text-green-700" : "text-red-700"
                                )}>
                                  {renderFormattedContent(item.content)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    else if (item.type === 'bullet-list') {
                      return (
                        <div key={i} className="mt-4 mb-4">
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-200 shadow-sm">
                            <ul className="space-y-3">
                              {item.points.map((point, j) => (
                                <li key={j} className="flex items-start gap-3 text-slate-700">
                                  <div className="w-2 h-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mt-3 flex-shrink-0"></div>
                                  <div className="leading-relaxed">
                                    {renderFormattedContent(point)}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      );
                    }
                    else if (item.type === 'emphasized-text') {
                      return (
                        <div key={i} className="mt-4 mb-4">
                          <div className="bg-yellow-50 p-5 rounded-xl border border-yellow-300 shadow-sm">
                            <div className="text-yellow-800 font-semibold">
                              {renderFormattedContent(item.content)}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    else if (item.type === 'additional-context') {
                      return (
                        <div key={i} className="mt-4 mb-4">
                          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-5 rounded-xl border border-amber-200 shadow-sm">
                            <div className="text-amber-800 leading-relaxed">
                              {renderFormattedContent(item.content)}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    else {
                      return (
                        <div key={i} className="mt-4 mb-4">
                          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="text-slate-700 leading-relaxed">
                              {renderFormattedContent(item.content)}
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-center gap-4">
          <Button
            variant="outline"
            onClick={onReset}
            className="flex items-center gap-2 px-5 py-2 border-gray-300"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </Button>
          
          <Button 
            onClick={onReset}
            className="bg-gradient-to-r from-primary/90 to-primary/70 hover:from-primary hover:to-primary text-white flex items-center gap-2 px-5 py-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Subjects
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default EnhancedResults;
